import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  clubs: [], // This will store the raw payed_chanel records
  subscribers: [], // This will store the club members
};

const slice = createSlice({
  name: 'club',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getClubsSuccess(state, action) {
      state.isLoading = false;
      state.clubs = action.payload;
    },
    saveClubSuccess(state) {
      state.isLoading = false;
    },
    deleteClubSuccess(state) {
      state.isLoading = false;
    },
    getSubscribersSuccess(state, action) {
      state.isLoading = false;
      state.subscribers = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function getClubs(shopid) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/payed_chanel/${shopid}`);
      // API returns a raw array [...] or { data: [...] }
      const clubsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      dispatch(slice.actions.getClubsSuccess(clubsData));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function saveClubTariffs(shopid, data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      // API currently expects a dictionary {}, not a list [].
      // If we have an array, we send them one by one or pick the first if it's single.
      if (Array.isArray(data)) {
        if (data.length === 0) return true;
        
        if (data.length === 1) {
          await axios.post(`/payed_chanel/${shopid}`, data[0]);
        } else {
          // Sequential or parallel execution for multiple records
          // Parallel for speed (max 10-20 requests usually)
          const requests = data.map(item => axios.post(`/payed_chanel/${shopid}`, item));
          await Promise.all(requests);
        }
      } else {
        await axios.post(`/payed_chanel/${shopid}`, data);
      }
      
      dispatch(slice.actions.saveClubSuccess());
      return true;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return false;
    }
  };
}

export function deleteClubTariff(shopid, tariffData) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      // tariffData should be { good_id, chanel_id }
      const response = await axios.delete(`/payed_chanel/${shopid}`, {
        data: tariffData
      });

      if (response.data.success === false) {
        dispatch(slice.actions.hasError(response.data.data || 'Failed to delete tariff'));
        return false;
      }

      dispatch(slice.actions.deleteClubSuccess());
      return true;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return false;
    }
  };
}

export function getSubscribers(shopid, chanelIds) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      // chanelIds can be a single ID or an array of IDs (Virtual Clubs)
      const ids = Array.isArray(chanelIds) ? chanelIds : [chanelIds];
      
      if (ids.length === 0) {
        dispatch(slice.actions.getSubscribersSuccess([]));
        return;
      }

      // Fetch all in parallel
      const requests = ids.map(id => axios.get(`/channel_users/${shopid}/${id}`));
      const results = await Promise.all(requests);
      
      // Merge results and add channel ID to each user record
      const allSubscribers = [];
      results.forEach((response, index) => {
        const users = Array.isArray(response.data) ? response.data : [];
        users.forEach(user => {
          allSubscribers.push({
            ...user,
            source_chanel_id: ids[index] // tagging so we can filter by channel in UI
          });
        });
      });

      dispatch(slice.actions.getSubscribersSuccess(allSubscribers));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
